import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isAccessPreviewFrame } from '../permissions/access';

const STORAGE_KEY = 'b2b41c.openTabs';

const TabsContext = createContext(null);

const readTabsFromStorage = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .filter(t => t && typeof t.url === 'string' && typeof t.title === 'string' && typeof t.id === 'string')
            .map(t => ({ ...t, kind: typeof t.kind === 'string' ? t.kind : null }));
    } catch (error) {
        console.warn('Failed to read open tabs from storage:', error);
        return [];
    }
};

const writeTabsToStorage = (tabs) => {
    if (isAccessPreviewFrame()) return;
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(tabs));
    } catch (error) {
        console.warn('Failed to write open tabs to storage:', error);
    }
};

const makeTabId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `tab_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const LOGIN_PATHS = ['/login', '/unauthorized'];
const isPinnablePath = (pathname) => {
    if (!pathname) return false;
    return !LOGIN_PATHS.some(p => pathname === p || pathname.startsWith(`${p}/`));
};

const AUTO_TAB_CAP = 15;

// Oldest-opened tabs are evicted first once the bar exceeds the cap; the tab
// for keepUrl (the page being opened right now) is never evicted.
const enforceCap = (list, keepUrl) => {
    if (list.length <= AUTO_TAB_CAP) return list;
    let next = list;
    const removable = [...next]
        .filter(t => t.url !== keepUrl)
        .sort((a, b) => (a.openedAt || 0) - (b.openedAt || 0));
    while (next.length > AUTO_TAB_CAP && removable.length) {
        const evict = removable.shift();
        next = next.filter(t => t.id !== evict.id);
    }
    return next;
};

const titleFromPath = (pathname) => {
    const segs = pathname.split('/').filter(Boolean);
    if (!segs.length) return 'Home';
    return segs
        .map(s => (/^\d+$/.test(s) ? `#${s}` : s.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())))
        .join(' › ');
};

export const TabsProvider = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [tabs, setTabs] = useState(() => readTabsFromStorage());
    const currentPageRef = useRef({ title: null, kind: null });

    useEffect(() => {
        writeTabsToStorage(tabs);
    }, [tabs]);

    // Browser-style tabs: every visited page pins itself to the footer bar,
    // capped at AUTO_TAB_CAP. Keyed on PATHNAME (not query string) so pages
    // that drive inner tabs via ?tab= don't spawn one footer tab per switch.
    // The page's useRegisterTabTitle call upgrades the placeholder title.
    useEffect(() => {
        if (!isPinnablePath(location.pathname)) return;
        const url = location.pathname + (location.search || '');
        setTabs(prev => {
            if (prev.some(t => t.url.split('?')[0] === location.pathname)) return prev;
            const added = [...prev, {
                id: makeTabId(),
                title: titleFromPath(location.pathname),
                url,
                kind: null,
                openedAt: Date.now(),
            }];
            return enforceCap(added, url);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const setCurrentPage = useCallback(({ title, kind }) => {
        const safeTitle = title || null;
        const safeKind = kind || null;
        currentPageRef.current = { title: safeTitle, kind: safeKind };
        if (!safeTitle) return;
        const currentUrl = location.pathname + (location.search || '');
        setTabs(prev => {
            const existing = prev.find(t => t.url === currentUrl)
                || prev.find(t => t.url.split('?')[0] === location.pathname);
            if (!existing) return prev;
            if (existing.title === safeTitle && existing.kind === safeKind && existing.url === currentUrl) return prev;
            return prev.map(t => t.id === existing.id ? { ...t, title: safeTitle, kind: safeKind, url: currentUrl } : t);
        });
    }, [location.pathname, location.search]);

    const openTab = useCallback(({ url, title, kind }) => {
        if (!url) return;
        const safeTitle = title || url;
        const safeKind = kind || null;
        const originUrl = location.pathname + (location.search || '');
        const { title: originTitle, kind: originKind } = currentPageRef.current;
        const canPinOrigin = isPinnablePath(location.pathname) && originUrl !== url;

        setTabs(prev => {
            let next = prev;

            if (canPinOrigin && originTitle) {
                const existingOrigin = next.find(t => t.url === originUrl);
                if (existingOrigin) {
                    if (existingOrigin.title !== originTitle || existingOrigin.kind !== originKind) {
                        next = next.map(t => t.url === originUrl ? { ...t, title: originTitle, kind: originKind } : t);
                    }
                } else {
                    next = [...next, { id: makeTabId(), title: originTitle, url: originUrl, kind: originKind, openedAt: Date.now() }];
                }
            }

            const existing = next.find(t => t.url === url);
            if (existing) {
                if (existing.title !== safeTitle || existing.kind !== safeKind) {
                    next = next.map(t => t.url === url ? { ...t, title: safeTitle, kind: safeKind } : t);
                }
            } else {
                next = [...next, { id: makeTabId(), title: safeTitle, url, kind: safeKind, openedAt: Date.now() }];
            }

            return enforceCap(next, url);
        });
        navigate(url);
    }, [navigate, location.pathname, location.search]);

    const closeTab = useCallback((id) => {
        setTabs(prev => prev.filter(t => t.id !== id));
    }, []);

    const closeAllTabs = useCallback(() => {
        setTabs([]);
    }, []);

    const activeTabId = useMemo(() => {
        const match = tabs.find(t => t.url === location.pathname + location.search)
            || tabs.find(t => t.url === location.pathname);
        return match ? match.id : null;
    }, [tabs, location.pathname, location.search]);

    const value = useMemo(() => ({
        tabs,
        activeTabId,
        openTab,
        closeTab,
        closeAllTabs,
        setCurrentPage,
    }), [tabs, activeTabId, openTab, closeTab, closeAllTabs, setCurrentPage]);

    return (
        <TabsContext.Provider value={value}>
            {children}
        </TabsContext.Provider>
    );
};

export const useTabs = () => {
    const ctx = useContext(TabsContext);
    if (!ctx) {
        throw new Error('useTabs must be used within a TabsProvider');
    }
    return ctx;
};

export const useRegisterTabTitle = (title, kind = null) => {
    const { setCurrentPage } = useTabs();
    useEffect(() => {
        setCurrentPage({ title, kind });
        return () => setCurrentPage({ title: null, kind: null });
    }, [title, kind, setCurrentPage]);
};
